// WebGPU detection (FR-060, research R-12). Each failure carries a typed reason
// so the fallback page can say plainly why the globe isn't running.

// The defaults from the WebGPU spec. Research R-12 found these are enough:
// 1M points x 16 B is 16 MB against a 128 MiB storage binding. Only the first
// figure is sourced; the rest are the spec defaults, kept as a floor.
export const REQUIRED_LIMITS = {
  maxStorageBufferBindingSize: 134_217_728,
  maxBufferSize: 268_435_456,
  maxTextureDimension2D: 8192,
  maxStorageBuffersPerShaderStage: 8,
  maxBindGroups: 4,
} as const satisfies Partial<Record<keyof GPUSupportedLimits, number>>;

export type RequiredLimit = keyof typeof REQUIRED_LIMITS;

export type GpuFailureReason = 'no-webgpu' | 'no-adapter' | 'limits' | 'device-failed' | 'device-lost';

export interface GpuFailure {
  readonly ok: false;
  readonly reason: GpuFailureReason;
  readonly message: string;
  // Diagnostic text for logs (the limits that fell short, the browser's error). Never shown raw.
  readonly detail?: string;
}

export interface GpuReady {
  readonly ok: true;
  readonly device: GPUDevice;
  readonly adapter: GPUAdapter;
}

export type GpuDetection = GpuReady | GpuFailure;

export const GPU_FAILURE_MESSAGES: Readonly<Record<GpuFailureReason, string>> = {
  'no-webgpu': 'Your browser does not support WebGPU, which the globe needs to draw.',
  'no-adapter': 'Your browser supports WebGPU, but it could not find a graphics card to use.',
  limits: 'Your graphics card is too small for the globe. It needs more memory than this device offers.',
  'device-failed': 'Your browser could not start the graphics card for the globe.',
  'device-lost': 'The graphics card stopped responding, so the globe had to stop.',
};

function fail(reason: GpuFailureReason, detail?: string): GpuFailure {
  return detail === undefined
    ? { ok: false, reason, message: GPU_FAILURE_MESSAGES[reason] }
    : { ok: false, reason, message: GPU_FAILURE_MESSAGES[reason], detail };
}

function errorText(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

// The limits the adapter offers below what the app needs, as "name: have < need".
export function missingLimits(limits: GPUSupportedLimits): string[] {
  const short: string[] = [];
  for (const [name, need] of Object.entries(REQUIRED_LIMITS) as [RequiredLimit, number][]) {
    const have = limits[name];
    if (typeof have !== 'number' || have < need) short.push(`${name}: ${String(have)} < ${need}`);
  }
  return short;
}

function defaultGpu(): GPU | undefined {
  return (globalThis.navigator as Navigator | undefined)?.gpu ?? undefined;
}

// Runs the checks in the order research R-12 sets. Never throws.
export async function detectWebGpu(gpu: GPU | undefined = defaultGpu()): Promise<GpuDetection> {
  if (!gpu) return fail('no-webgpu');

  let adapter: GPUAdapter | null;
  try {
    adapter = await gpu.requestAdapter({ powerPreference: 'high-performance' });
  } catch (err) {
    return fail('no-adapter', errorText(err));
  }
  if (!adapter) return fail('no-adapter');

  const short = missingLimits(adapter.limits);
  if (short.length > 0) return fail('limits', short.join('; '));

  let device: GPUDevice;
  try {
    device = await adapter.requestDevice({ requiredLimits: { ...REQUIRED_LIMITS } });
  } catch (err) {
    return fail('device-failed', errorText(err));
  }
  return { ok: true, device, adapter };
}

// Calls back once if the device is lost for any reason other than our own destroy().
export function watchDeviceLost(device: GPUDevice, onLost: (failure: GpuFailure) => void): void {
  void device.lost.then((info) => {
    if (info.reason === 'destroyed') return;
    onLost(fail('device-lost', info.message || undefined));
  });
}
